/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { HeroModule, MODULE_LAYOUTS } from '@coursera-web/cds-labs';

import { FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';
import { isRightToLeft } from 'js/lib/language';

import { Hidden, Typography2, breakpoints } from '@coursera/cds-core';

import ReactPriceDisplay from 'bundles/ownable-products-common/components/ReactPriceDisplay';
import type { ConsumerPathwayRolePageDataContextType } from 'bundles/unified-career-academy/contexts/ConsumerPathwayRolePageDataContext';
import FormattedList from 'bundles/unified-common/components/FormattedList';

import _t from 'i18n!nls/career-academy';

type Props = {
  pageData: ConsumerPathwayRolePageDataContextType;
};

const styles = {
  description: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);

    ${breakpoints.up('md')} {
      width: 80%;
    }
  `,
  imageOverlayWrapper: css`
    position: absolute;
    display: flex;
    justify-content: center;

    ${breakpoints.down('xs')} {
      bottom: calc(var(--cds-spacing-200) * -1);
      left: 0;
      right: 0;
    }

    ${breakpoints.up('sm')} {
      left: 0;
      bottom: 0;
      margin-right: var(--cds-spacing-200);
    }

    ${breakpoints.up('md')} {
      right: 40%;
      bottom: 50px;
      margin-right: 0;
    }
  `,
  imageOverlay: ({ borderColor }: { borderColor?: string | null }) => css`
    border: 2px solid ${borderColor ?? 'var(--cds-color-neutral-primary-weak)'};
    border-radius: var(--cds-border-radius-max);
    background: var(--cds-color-neutral-background-primary);
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
  `,
  dotSeparator: css`
    padding: 0 var(--cds-spacing-50);
  `,
  stats: (isRTL: boolean) => css`
    color: var(--cds-color-neutral-primary-weak);

    // Targeting the direct children, i.e. all spans inside the Typography2 component,
    // in RTL mode to display them inline-block to prevent the spans from being placed in random places
    > span {
      display: ${isRTL ? 'inline-block' : 'inline'};
    }
  `,
  statsItem: css`
    display: inline-block;
    vertical-align: bottom;
  `,
};

function HeroModuleRenderer({ pageData }: Props) {
  const {
    name,
    skills,
    description,
    jobPostingData,
    imagePrimaryColor,
    heroImage,
    heroImageMobile,
    merchandisingSkillNames,
  } = pageData?.careerRoleData ?? {};

  const { medianSalary, numJobsAvailable, currencyCode } = jobPostingData ?? {};

  if (!name) return null;

  let skillsToShow = skills?.map((skill) => skill.skillName).filter(Boolean) ?? [];
  skillsToShow = skillsToShow.slice(0, 8);

  const preferences = merchandisingSkillNames?.slice(0, 3);

  const descriptionToShow = (
    <div css={styles.description}>
      {!!preferences?.length && (
        <Typography2 variant="subtitleMedium" component="p">
          <FormattedMessage
            message={_t('If you like {skills} this role is for you.')}
            skills={<FormattedList style="long" type="conjunction" list={preferences} />}
          />
        </Typography2>
      )}
      {description && (
        <Typography2 variant="bodySecondary" component="p">
          {description}
        </Typography2>
      )}
      {!!skillsToShow.length && (
        <Typography2 variant="bodySecondary" component="p">
          <FormattedMessage
            message={_t('{label}: {skills}')}
            label={<b>{_t('Skills you’ll need')}</b>}
            skills={<FormattedList style="narrow" type="conjunction" list={skillsToShow} />}
          />
        </Typography2>
      )}
    </div>
  );

  const imageOverlayContent =
    medianSalary || numJobsAvailable ? (
      <Typography2 variant="bodyPrimary" component="div" css={styles.stats(isRightToLeft(_t.getLocale()))}>
        {medianSalary && currencyCode && (
          <FormattedMessage
            message={_t('{medianSalary} median salary')}
            medianSalary={
              <Typography2 variant="subtitleLarge" component="b" css={styles.statsItem}>
                <ReactPriceDisplay value={medianSalary} currency={currencyCode} hideCurrencyCode />
              </Typography2>
            }
          />
        )}
        {/* Add a separator if both median salary and numJobsAvailable are present */}
        {medianSalary && currencyCode && numJobsAvailable && (
          <span aria-hidden css={styles.dotSeparator}>
            &#183;
          </span>
        )}
        {numJobsAvailable && (
          <FormattedMessage
            message={_t('{numberOfJobs} jobs available¹')}
            numberOfJobs={
              <Typography2 variant="subtitleLarge" component="b" css={styles.statsItem}>
                <FormattedNumber value={numJobsAvailable} />
              </Typography2>
            }
          />
        )}
      </Typography2>
    ) : null;

  const imageOverlay = imageOverlayContent ? (
    <div css={styles.imageOverlayWrapper}>
      <div css={styles.imageOverlay({ borderColor: imagePrimaryColor })}>{imageOverlayContent}</div>
    </div>
  ) : undefined;

  const getHeroModuleProps = ({ isMobile }: { isMobile: boolean }): React.ComponentProps<typeof HeroModule> => ({
    headline: name,
    description: descriptionToShow,
    image: {
      url: isMobile ? heroImageMobile?.url : heroImage?.url,
      width: isMobile ? 600 : 720,
    },
    showBottomBorder: !isMobile,
    layout: MODULE_LAYOUTS.text6image6,
    showImagesAboveContentOnMobile: false,
    imageOverlay,
    breadcrumbs: {
      items: [
        { label: _t('Home'), href: '/' },
        { label: _t('Careers'), href: '/career-academy' },
      ],
    },
  });

  return (
    <React.Fragment>
      <Hidden smUp>
        <HeroModule {...getHeroModuleProps({ isMobile: true })} />
      </Hidden>
      <Hidden xsDown>
        <HeroModule {...getHeroModuleProps({ isMobile: false })} />
      </Hidden>
    </React.Fragment>
  );
}

export default HeroModuleRenderer;
